import { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '@/components/layout/DashboardLayout';
import BackButton from '@/components/BackButton';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar, ExternalLink, Info } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CalendarPage = () => {
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCoaches();
  }, []);

  const fetchCoaches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/coaches`);
      setCoaches(response.data);
      if (response.data.length > 0) {
        setSelectedCoach(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to fetch coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="calendar-page">
        <BackButton to="/dashboard" label="Back to Dashboard" />
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-heading text-3xl lg:text-4xl font-bold text-forest">
            Schedule a Session
          </h1>
          <p className="text-gray-600 mt-2">
            Book your coaching sessions with Calendly integration.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Coach Selection */}
          <div className="lg:col-span-1">
            <Card className="card-base">
              <CardContent className="p-4">
                <h2 className="font-heading text-lg font-semibold text-forest mb-4">Select a Coach</h2>
                
                {loading ? (
                  <div className="space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="h-16 bg-sand rounded-lg animate-pulse" />
                    ))}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {coaches.map((coach) => (
                      <button
                        key={coach.id}
                        onClick={() => setSelectedCoach(coach)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                          selectedCoach?.id === coach.id
                            ? 'bg-forest text-white'
                            : 'bg-sand/50 hover:bg-sand text-forest'
                        }`}
                        data-testid={`select-coach-${coach.id}`}
                      >
                        <img 
                          src={coach.image_url} 
                          alt={coach.name}
                          className="h-10 w-10 rounded-full object-cover"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{coach.name}</p>
                          <p className={`text-sm truncate ${selectedCoach?.id === coach.id ? 'text-white/70' : 'text-gray-500'}`}>
                            {coach.title}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Calendly Embed */}
          <div className="lg:col-span-2">
            <Card className="card-base">
              <CardContent className="p-6">
                {selectedCoach ? (
                  <>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="font-heading text-xl font-semibold text-forest">
                          Book with {selectedCoach.name}
                        </h2>
                        <p className="text-gray-500">{selectedCoach.title}</p>
                      </div>
                      {selectedCoach.calendly_link && (
                        <a 
                          href={selectedCoach.calendly_link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          <Button variant="outline" className="btn-outline">
                            Open in Calendly <ExternalLink className="ml-2 h-4 w-4" />
                          </Button>
                        </a>
                      )}
                    </div>

                    {/* Calendly Widget Placeholder */}
                    <div className="calendly-container bg-sand/30 flex items-center justify-center">
                      <div className="text-center p-8">
                        <Calendar className="h-16 w-16 text-forest mx-auto mb-4" />
                        <h3 className="font-heading text-xl font-semibold text-forest mb-2">
                          Calendly Integration
                        </h3>
                        <p className="text-gray-600 mb-6 max-w-md">
                          To book a session with {selectedCoach.name}, please use their Calendly scheduling page.
                        </p>
                        {selectedCoach.calendly_link ? (
                          <a 
                            href={selectedCoach.calendly_link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                          >
                            <Button className="btn-primary">
                              Schedule Now <ExternalLink className="ml-2 h-4 w-4" />
                            </Button>
                          </a>
                        ) : (
                          <div className="bg-sand/50 rounded-lg p-4 inline-flex items-center gap-2">
                            <Info className="h-5 w-5 text-terracotta" />
                            <span className="text-gray-600">Calendly link not yet configured</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Info Box */}
                    <div className="mt-6 p-4 bg-sand/50 rounded-lg">
                      <h4 className="font-medium text-forest mb-2">How Scheduling Works</h4>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li>1. Click "Schedule Now" to open the Calendly booking page</li>
                        <li>2. Select an available time slot that works for you</li>
                        <li>3. Enter your details and confirm the booking</li>
                        <li>4. You'll receive a confirmation email with meeting details</li>
                      </ul>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-12">
                    <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">Select a coach to view scheduling options</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CalendarPage;
